import { useEffect, useState } from 'react';
import API, { fmt } from '@/lib/shopApi';
import { Receipt, Package, ClipboardText, MicrosoftExcelLogo, X } from '@phosphor-icons/react';

const TYPE_META = {
  bestelbon: { label: 'Bestelbon', icon: ClipboardText, color: '#7C3AED' },
  leverbon: { label: 'Leverbon', icon: Package, color: '#F59E0B' },
  factuur: { label: 'Factuur', icon: Receipt, color: '#059669' },
};
const STATUS_LABEL = {
  concept: { label: 'In behandeling', color: '#6B7280' },
  bevestigd: { label: 'Bevestigd', color: '#0EA5E9' },
  'productie in afwachting': { label: 'In productie', color: '#F59E0B' },
  klaar: { label: 'Klaar', color: '#16A34A' },
  geleverd: { label: 'Geleverd', color: '#16A34A' },
  openstaand: { label: 'Openstaand', color: '#DC2626' },
  betaald: { label: 'Betaald', color: '#16A34A' },
};

export default function ShopBestellingenPage({ filter }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [excelOpen, setExcelOpen] = useState(false);
  const [excelVan, setExcelVan] = useState('');
  const [excelTot, setExcelTot] = useState('');
  const [excelLoading, setExcelLoading] = useState(false);

  useEffect(() => {
    API.get('/shop/orders').then((r) => setOrders(r.data.orders || [])).finally(() => setLoading(false));
  }, []);

  const filtered = filter === 'facturen'
    ? orders.filter((o) => o.type === 'factuur')
    : orders.filter((o) => o.type !== 'factuur');

  const downloadExcel = async () => {
    setExcelLoading(true);
    try {
      const params = {};
      if (excelVan) params.van = excelVan;
      if (excelTot) params.tot = excelTot;
      const r = await API.get('/shop/aankoophistoriek-excel', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mijn_aankopen_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      setExcelOpen(false);
    } catch (e) {
      alert('Fout bij download: ' + (e.response?.data?.detail || e.message));
    } finally {
      setExcelLoading(false);
    }
  };

  return (
    <div data-testid="shop-orders-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-black">
          {filter === 'facturen' ? 'Mijn facturen' : 'Mijn bestellingen'}
        </h1>
        <button
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          onClick={() => { setExcelVan(''); setExcelTot(''); setExcelOpen(true); }}
          data-testid="shop-excel-export-btn"
          title="Download Excel-overzicht van al uw aankopen"
        >
          <MicrosoftExcelLogo size={16} weight="fill" /> Excel-export
        </button>
      </div>
      {loading ? (
        <div className="text-center text-gray-400 py-12">Laden…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          {filter === 'facturen' ? 'U heeft nog geen facturen.' : 'U heeft nog geen bestellingen geplaatst.'}
        </div>
      ) : (
        <div className="bg-white border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase text-gray-500">
                <th className="px-3 py-2">Nummer</th>
                <th className="px-3 py-2">Datum</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Totaal</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const t = TYPE_META[o.type] || { label: o.type, icon: Receipt, color: '#6B7280' };
                const Icon = t.icon;
                const s = STATUS_LABEL[(o.status || '').toLowerCase()] || { label: o.status || '—', color: '#6B7280' };
                return (
                  <tr key={o.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`shop-order-row-${o.id}`}>
                    <td className="px-3 py-2 font-mono font-bold">{o.id}</td>
                    <td className="px-3 py-2 text-xs">{o.datum}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: t.color }}>
                        <Icon size={12} weight="bold" /> {t.label}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded" style={{ background: `${s.color}15`, color: s.color }}>{s.label}</span>
                    </td>
                    <td className="px-3 py-2 text-right font-bold">{fmt(o.totaal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {excelOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !excelLoading && setExcelOpen(false)}
          data-testid="shop-excel-modal"
        >
          <div className="bg-white shadow-2xl border border-gray-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <MicrosoftExcelLogo size={22} weight="fill" className="text-emerald-600" />
                Mijn aankopen — Excel
              </h3>
              <button className="text-gray-400 hover:text-gray-700" onClick={() => !excelLoading && setExcelOpen(false)} disabled={excelLoading}>
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Download een Excel-bestand met al uw aankopen — handig voor boekhouding of analyse.
            </p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Van datum (optioneel)</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={excelVan}
                  onChange={(e) => setExcelVan(e.target.value)}
                  disabled={excelLoading}
                  data-testid="shop-excel-van"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Tot datum (optioneel)</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={excelTot}
                  onChange={(e) => setExcelTot(e.target.value)}
                  disabled={excelLoading}
                  data-testid="shop-excel-tot"
                />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" className="text-[11px] px-2 py-1 border border-gray-300 hover:bg-gray-50" onClick={() => { setExcelVan(''); setExcelTot(''); }} disabled={excelLoading}>Alles</button>
                <button type="button" className="text-[11px] px-2 py-1 border border-gray-300 hover:bg-gray-50" onClick={() => {
                  const d = new Date(); const start = new Date(d.getFullYear(), d.getMonth(), 1);
                  setExcelVan(start.toISOString().slice(0, 10)); setExcelTot(d.toISOString().slice(0, 10));
                }} disabled={excelLoading}>Deze maand</button>
                <button type="button" className="text-[11px] px-2 py-1 border border-gray-300 hover:bg-gray-50" onClick={() => {
                  const d = new Date(); const start = new Date(d.getFullYear(), 0, 1);
                  setExcelVan(start.toISOString().slice(0, 10)); setExcelTot(d.toISOString().slice(0, 10));
                }} disabled={excelLoading}>Dit jaar</button>
                <button type="button" className="text-[11px] px-2 py-1 border border-gray-300 hover:bg-gray-50" onClick={() => {
                  const d = new Date(); const start = new Date(d); start.setMonth(d.getMonth() - 12);
                  setExcelVan(start.toISOString().slice(0, 10)); setExcelTot(d.toISOString().slice(0, 10));
                }} disabled={excelLoading}>Laatste 12m</button>
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mb-4">
              Het Excel-bestand bevat 2 tabbladen: <strong>Overzicht</strong> (per artikel) + <strong>Detail</strong> (per regel).
            </p>
            <div className="flex justify-end gap-2">
              <button className="px-3 py-2 text-sm font-bold border border-gray-300 hover:bg-gray-50" onClick={() => setExcelOpen(false)} disabled={excelLoading}>Annuleer</button>
              <button
                className="px-3 py-2 text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                onClick={downloadExcel}
                disabled={excelLoading}
                data-testid="shop-excel-download"
              >
                {excelLoading ? 'Bezig…' : 'Download Excel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
